import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { FileText, ArrowUpDown, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const STATUTS_LABELS = {
  en_attente: { label: 'En attente', cls: 'bg-gray-100 text-gray-700' },
  envoye: { label: 'Envoyé', cls: 'bg-blue-100 text-blue-700' },
  demande_envoyee: { label: 'Demande envoyée', cls: 'bg-blue-100 text-blue-700' },
  docs_complementaires: { label: 'Docs complémentaires', cls: 'bg-amber-100 text-amber-700' },
  en_instruction: { label: 'En instruction', cls: 'bg-indigo-100 text-indigo-700' },
  accepte: { label: 'Accepté', cls: 'bg-emerald-100 text-emerald-700' },
  subvention_recue: { label: 'Subvention reçue', cls: 'bg-green-100 text-green-800' },
  refuse: { label: 'Refusé', cls: 'bg-red-100 text-red-700' },
};

const CMD_STATUTS_LABELS = {
  incomplet: { label: 'Incomplet', cls: 'bg-gray-100 text-gray-600' },
  incomplet_j15: { label: 'Incomplet J+15', cls: 'bg-orange-100 text-orange-700' },
  incomplet_j30: { label: 'Incomplet J+30', cls: 'bg-red-100 text-red-700' },
  pret_a_livrer: { label: 'Prêt à livrer', cls: 'bg-blue-100 text-blue-700' },
  livre: { label: 'Livré', cls: 'bg-indigo-100 text-indigo-700' },
  demande_envoyee: { label: 'Demande envoyée', cls: 'bg-purple-100 text-purple-700' },
  subvention_recue: { label: 'Subvention reçue', cls: 'bg-green-100 text-green-800' },
  facture_reglee: { label: 'Facture réglée', cls: 'bg-teal-100 text-teal-700' },
  paye_par_ne: { label: 'Payé par NE', cls: 'bg-cyan-100 text-cyan-700' },
};

const PAGE_SIZE = 20;

export default function SuiviCarsat() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Restore state from URL params
  const search = searchParams.get('q') || '';
  const statutFilter = searchParams.get('statut') || '';
  const sortField = searchParams.get('sort') || 'date_demande';
  const sortDir = searchParams.get('dir') || 'desc';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (!v || v === '' || (k === 'page' && v === 1) || (k === 'sort' && v === 'date_demande') || (k === 'dir' && v === 'desc')) {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    api.get('/carsat/suivi').then(r => setDossiers(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return '—'; }
  };

  const getDaysSince = (d, statut, dateReception) => {
    if (!d) return null;
    try {
      const start = new Date(d).getTime();
      // Si subvention reçue, on arrête le compteur à la date de réception
      const end = (statut === 'subvention_recue' && dateReception)
        ? new Date(dateReception).getTime()
        : Date.now();
      return Math.floor((end - start) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };

  const getDaysColor = (days) => {
    if (days === null) return '';
    if (days < 60) return 'text-emerald-700 bg-emerald-50';
    if (days <= 90) return 'text-orange-700 bg-orange-50';
    return 'text-red-700 bg-red-50';
  };

  const formatMontant = (m) => {
    if (!m && m !== 0) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(m);
  };

  const filtered = dossiers
    .filter(d => {
      if (search) {
        const q = search.toLowerCase();
        if (!(d.client_nom?.toLowerCase().includes(q) || d.numero_dossier?.toLowerCase().includes(q) || d.commande_ref?.toLowerCase().includes(q))) return false;
      }
      if (statutFilter && d.statut !== statutFilter) return false;
      return true;
    })
    .sort((a, b) => {
      let va, vb;
      if (sortField === 'date_demande') {
        va = a.date_demande || a.date_envoi || '';
        vb = b.date_demande || b.date_envoi || '';
      } else if (sortField === 'montant_subvention') {
        va = a[sortField] || 0;
        vb = b[sortField] || 0;
      } else {
        va = a[sortField] || '';
        vb = b[sortField] || '';
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field) => {
    if (sortField === field) {
      updateParams({ dir: sortDir === 'asc' ? 'desc' : 'asc', page: 1 });
    } else {
      updateParams({ sort: field, dir: 'desc', page: 1 });
    }
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 select-none"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        <ArrowUpDown size={12} className={sortField === field ? 'text-gray-900' : 'text-gray-300'} />
      </span>
    </th>
  );

  // Stats summary
  const totalSubvention = dossiers.reduce((s, d) => s + (d.montant_subvention || 0), 0);
  const nbEnvoyes = dossiers.filter(d => d.statut !== 'en_attente').length;
  const nbAcceptes = dossiers.filter(d => ['accepte', 'subvention_recue'].includes(d.statut)).length;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="suivi-carsat-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suivi CARSAT</h1>
          <p className="text-sm text-gray-500 mt-1">Suivi de tous les dossiers de subvention</p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="stat-total-dossiers">
          <p className="text-xs font-medium text-gray-500 uppercase">Total dossiers</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{dossiers.length}</p>
          <p className="text-xs text-gray-400 mt-1">{nbEnvoyes} envoyé{nbEnvoyes > 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="stat-acceptes">
          <p className="text-xs font-medium text-gray-500 uppercase">Acceptés / Reçus</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{nbAcceptes}</p>
          <p className="text-xs text-gray-400 mt-1">sur {dossiers.length} dossiers</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="stat-montant-total">
          <p className="text-xs font-medium text-gray-500 uppercase">Subventions totales</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatMontant(totalSubvention)}</p>
          <p className="text-xs text-gray-400 mt-1">tous dossiers confondus</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher client, n° dossier..."
            value={search}
            onChange={e => updateParams({ q: e.target.value, page: 1 })}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 outline-none"
            data-testid="search-carsat"
          />
        </div>
        <div className="relative">
          <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={statutFilter}
            onChange={e => updateParams({ statut: e.target.value, page: 1 })}
            className="pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm bg-white appearance-none cursor-pointer focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 outline-none"
            data-testid="filter-statut-carsat"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(STATUTS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="carsat-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <SortHeader field="numero_dossier">N° Dossier</SortHeader>
                <SortHeader field="client_nom">Client</SortHeader>
                <SortHeader field="date_demande">Date de la demande</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Jours</th>
                <SortHeader field="statut">Statut CARSAT</SortHeader>
                <SortHeader field="montant_subvention">Subvention</SortHeader>
                <SortHeader field="commande_statut">Statut commande</SortHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    <FileText size={32} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Aucun dossier trouvé</p>
                  </td>
                </tr>
              ) : paginated.map(d => {
                const sCarsat = STATUTS_LABELS[d.statut] || { label: d.statut, cls: 'bg-gray-100 text-gray-600' };
                const sCmd = CMD_STATUTS_LABELS[d.commande_statut] || { label: d.commande_statut || '—', cls: 'bg-gray-100 text-gray-600' };
                const days = getDaysSince(d.date_demande || d.date_envoi, d.statut, d.date_reception_subvention);
                const isFrozen = d.statut === 'subvention_recue';
                const daysColor = isFrozen ? 'text-emerald-700 bg-emerald-50' : getDaysColor(days);
                return (
                  <tr
                    key={d.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/clients/${d.client_id}`)}
                    data-testid={`carsat-row-${d.id}`}
                  >
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono font-medium text-gray-900">{d.numero_dossier || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{d.client_nom}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{formatDate(d.date_demande || d.date_envoi)}</span>
                    </td>
                    <td className="px-4 py-3">
                      {days !== null ? (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${daysColor}`} data-testid={`days-badge-${d.id}`}>
                          {days}j
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${sCarsat.cls}`}>
                        {sCarsat.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-900">{d.montant_subvention ? formatMontant(d.montant_subvention) : '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${sCmd.cls}`}>
                        {sCmd.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">{filtered.length} dossier{filtered.length > 1 ? 's' : ''}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => updateParams({ page: Math.max(1, page - 1) })}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="carsat-prev-page"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs text-gray-600 font-medium">{page} / {totalPages}</span>
              <button
                onClick={() => updateParams({ page: Math.min(totalPages, page + 1) })}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="carsat-next-page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
